import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTour } from "../contexts/TourContext";
import { Button } from "./ui/button";
import { ArrowLeft, ArrowRight, X, Sparkle } from "@phosphor-icons/react";

const PADDING = 10;
const TOOLTIP_GAP = 14;

function getRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  };
}

function computeTooltipPos(rect, placement, tipSize) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) {
    // Centered modal
    return {
      top: Math.max(20, (vh - tipSize.h) / 2),
      left: Math.max(20, (vw - tipSize.w) / 2),
    };
  }
  let top = 0;
  let left = 0;
  switch (placement) {
    case "right":
      top = rect.top + rect.height / 2 - tipSize.h / 2;
      left = rect.left + rect.width + TOOLTIP_GAP;
      break;
    case "left":
      top = rect.top + rect.height / 2 - tipSize.h / 2;
      left = rect.left - tipSize.w - TOOLTIP_GAP;
      break;
    case "bottom":
      top = rect.top + rect.height + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tipSize.w / 2;
      break;
    case "top":
    default:
      top = rect.top - tipSize.h - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tipSize.w / 2;
      break;
  }
  // Clamp into viewport
  top = Math.min(Math.max(20, top), vh - tipSize.h - 20);
  left = Math.min(Math.max(20, left), vw - tipSize.w - 20);
  return { top, left };
}

export default function TourOverlay() {
  const { active, currentStep, step, total, next, prev, skip } = useTour();
  const tooltipRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [tipSize, setTipSize] = useState({ w: 360, h: 220 });
  const [tipPos, setTipPos] = useState({ top: 100, left: 100 });

  // Recompute target rect on every step + window resize/scroll
  useEffect(() => {
    if (!active || !currentStep) return undefined;
    const update = () => setRect(getRect(currentStep.selector));
    update();
    const t = setTimeout(update, 120); // settle layout
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, currentStep]);

  // Measure tooltip for positioning
  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const r = tooltipRef.current.getBoundingClientRect();
    setTipSize({ w: r.width, h: r.height });
  }, [step, currentStep]);

  // Recompute position whenever rect or tipSize changes
  useEffect(() => {
    if (!currentStep) return;
    setTipPos(computeTooltipPos(rect, currentStep.placement, tipSize));
  }, [rect, tipSize, currentStep]);

  // Scroll target into view when step changes
  useEffect(() => {
    if (!active || !currentStep?.selector) return;
    const el = document.querySelector(currentStep.selector);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [active, currentStep]);

  // Keyboard navigation
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") skip();
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, next, prev, skip]);

  if (!active || !currentStep) return null;

  const accent = currentStep.accent || "#2563EB";
  const isLast = step === total - 1;
  const isFirst = step === 0;

  return (
    <div className="fixed inset-0 z-[80]" data-testid="tour-overlay">
      {/* SVG mask backdrop with cut-out spotlight */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        onClick={skip}
        aria-hidden="true"
      >
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="14"
                ry="14"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(10, 11, 16, 0.78)"
          mask="url(#tour-mask)"
        />
        {rect && (
          <rect
            x={rect.left}
            y={rect.top}
            width={rect.width}
            height={rect.height}
            rx="14"
            ry="14"
            fill="none"
            stroke={accent}
            strokeWidth="2"
            strokeDasharray="6 6"
            className="animate-pulse"
          />
        )}
      </svg>

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        data-testid="tour-tooltip"
        style={{
          position: "absolute",
          top: tipPos.top,
          left: tipPos.left,
          width: 360,
          maxWidth: "calc(100vw - 40px)",
        }}
        className="bg-deck-sidebar border border-white/10 rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-6 animate-fade-up"
      >
        <div className="flex items-start justify-between mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center deck-tile shrink-0"
            style={{ backgroundColor: accent }}
          >
            <Sparkle size={18} weight="fill" className="text-white" />
          </div>
          <button
            onClick={skip}
            data-testid="tour-skip-x"
            title="Cerrar tour"
            className="text-white/40 hover:text-white transition w-7 h-7 flex items-center justify-center"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15] mb-1">
          Paso {step + 1} / {total}
        </div>
        <h3 className="text-xl font-heading font-black text-white mb-2 leading-tight">
          {currentStep.title}
        </h3>
        <p className="text-sm font-mono text-white/70 leading-relaxed">
          {currentStep.body}
        </p>

        {/* Progress bar */}
        <div className="mt-5 h-1 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${((step + 1) / total) * 100}%`,
              backgroundColor: accent,
            }}
          />
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            data-testid="tour-skip"
            onClick={skip}
            className="text-xs font-mono uppercase tracking-[0.12em] text-white/50 hover:text-white transition"
          >
            Saltar
          </button>
          <div className="flex gap-2">
            {!isFirst && (
              <Button
                data-testid="tour-prev"
                onClick={prev}
                variant="outline"
                size="sm"
                className="bg-white/5 border-white/15 text-white hover:bg-white/10"
              >
                <ArrowLeft size={14} weight="bold" className="mr-1" />
                Anterior
              </Button>
            )}
            <Button
              data-testid="tour-next"
              onClick={next}
              size="sm"
              className="font-heading font-bold text-white"
              style={{ backgroundColor: accent }}
            >
              {isLast ? "Terminar" : "Siguiente"}
              {!isLast && <ArrowRight size={14} weight="bold" className="ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
